import React from 'react';
import { HandHeart, Users, ArrowRight } from 'lucide-react';
import { howToHelpOptions } from '../mock';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const iconMap = {
  'hand-heart': HandHeart,
  'users': Users
};

const HowToHelp = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="help" className="py-20 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-100 rounded-full opacity-20 blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-100 rounded-full opacity-20 blur-3xl -z-10"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How You Can Help
          </h2>
          <div className="w-24 h-1 bg-teal-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 leading-relaxed">
            Your support can transform lives. Whether through donations or volunteering your time, 
            every contribution makes a real difference in our mission to feed those in need.
          </p>
        </div>

        {/* Help Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {howToHelpOptions.map((option) => {
            const IconComponent = iconMap[option.icon];
            return (
              <Card key={option.id} className="border-2 border-gray-100 hover:border-teal-400 hover:shadow-2xl transition-all duration-300 group overflow-hidden">
                <CardContent className="p-0">
                  <div className="p-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                      <IconComponent className="text-white" size={36} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {option.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {option.description}
                    </p>
                    <Button
                      onClick={scrollToContact}
                      className="bg-teal-600 hover:bg-teal-700 text-white group-hover:bg-emerald-600 transition-colors w-full sm:w-auto"
                    >
                      {option.action}
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                    </Button>
                  </div>
                  <div className="h-2 bg-gradient-to-r from-teal-500 to-emerald-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Featured Image Section */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img
            src="https://images.pexels.com/photos/6995247/pexels-photo-6995247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Volunteers preparing food"
            className="w-full h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 via-teal-800/70 to-transparent flex items-center">
            <div className="p-8 sm:p-12 max-w-2xl">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Join Our Community of Change-Makers
              </h3>
              <p className="text-gray-100 text-lg mb-6">
                Be part of something bigger. Your time, resources, and compassion can help us reach more people and create lasting impact.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Get Started Today
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToHelp;
